import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/ui/LanguageToggle';
import {
  Brain,
  BookOpen,
  Bell,
  ChevronRight,
  Check,
  Play,
  UserCheck,
  UserPlus,
  LineChart,
  Zap,
  TrendingUp,
  Building2,
  Crosshair,
  CandlestickChart,
  Sigma,
  Sparkles,
  ListChecks,
  FileText,
  Sliders,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedLogo from '../components/ui/AnimatedLogo';
import Button from '../components/ui/Button';
import { BRAND } from '../config/brand';

const featureIcons = [BookOpen, Brain, Bell];

const stepNumbers = ['01', '02', '03', '04'];

export default function Landing() {
  const { t } = useTranslation();
  const [showDemo, setShowDemo] = useState(false);

  const features = [
    {
      icon: featureIcons[0],
      title: t('landing.features.items.0.title'),
      description: t('landing.features.items.0.description'),
    },
    {
      icon: featureIcons[1],
      title: t('landing.features.items.1.title'),
      description: t('landing.features.items.1.description'),
    },
    {
      icon: featureIcons[2],
      title: t('landing.features.items.2.title'),
      description: t('landing.features.items.2.description'),
      badge: t('landing.features.items.2.badge'),
    },
  ];

  const steps = stepNumbers.map((number, i) => ({
    number,
    title: t(`landing.howItWorks.steps.${i}.title`),
    description: t(`landing.howItWorks.steps.${i}.description`),
  }));

  const stats = [
    { value: '100+', label: t('landing.stats.assetsAvailable') },
    { value: '4', label: t('landing.stats.methodsCount') },
    { value: '24/7', label: t('landing.stats.aiAnalysis') },
    { value: 'Expert', label: t('landing.stats.validatedSignals') },
  ];

  const methodColors = [
    { bg: 'from-amber-500/15 to-amber-600/5', icon: 'text-accent-gold', border: 'border-amber-500/30', dot: 'bg-amber-400' },
    { bg: 'from-violet-500/15 to-violet-600/5', icon: 'text-violet-400', border: 'border-violet-500/30', dot: 'bg-violet-400' },
    { bg: 'from-blue-500/15 to-blue-600/5', icon: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-400' },
    { bg: 'from-emerald-500/15 to-emerald-600/5', icon: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  ];

  const methodIcons = [Building2, Crosshair, CandlestickChart, Sigma];

  const methods = methodIcons.map((Icon, i) => ({
    Icon,
    title: t(`landing.methods.items.${i}.title`),
    short: t(`landing.methods.items.${i}.short`),
    description: t(`landing.methods.items.${i}.description`),
    tags: [
      t(`landing.methods.items.${i}.tags.0`),
      t(`landing.methods.items.${i}.tags.1`),
      t(`landing.methods.items.${i}.tags.2`),
      t(`landing.methods.items.${i}.tags.3`),
    ],
    color: methodColors[i],
  }));

  const promptIcons = [FileText, ListChecks, Sliders];
  const promptFeatures = promptIcons.map((Icon, i) => ({
    Icon,
    title: t(`landing.promptCustom.features.${i}.title`),
    description: t(`landing.promptCustom.features.${i}.description`),
  }));

  return (
    <div className="min-h-screen bg-bg-primary overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-bg-primary/80 backdrop-blur-xl border-b border-border-subtle">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.svg" alt={BRAND.displayDomain} className="h-8 w-auto" />
            <div className="flex items-baseline">
              <span className="font-display font-semibold text-xl tracking-tight text-white">{BRAND.name}</span>
              <span className="text-accent-primary font-display font-semibold text-xl tracking-tight">.ai</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-text-secondary hover:text-text-primary transition-colors">
              {t('landing.nav.features')}
            </a>
            <a href="#methods" className="text-text-secondary hover:text-text-primary transition-colors">
              {t('landing.nav.methods')}
            </a>
            <a href="#how-it-works" className="text-text-secondary hover:text-text-primary transition-colors">
              {t('landing.nav.howItWorks')}
            </a>
            <Link to="/pricing" className="text-text-secondary hover:text-text-primary transition-colors">
              {t('landing.nav.pricing')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageToggle variant="compact" />
            <Link to="/login">
              <Button variant="ghost" size="sm">{t('landing.nav.signIn')}</Button>
            </Link>
            <Link to="/register">
              <Button size="sm">{t('landing.nav.getStarted')}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Animated Logo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-8"
            >
              <AnimatedLogo size="xl" showText={false} />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-text-primary mb-6"
            >
              {t('landing.hero.titleBefore')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-primary to-accent-secondary">
                {t('landing.hero.titleHighlight')}
              </span>{' '}
              {t('landing.hero.titleAfter')}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xl md:text-2xl text-text-secondary mb-8 max-w-2xl mx-auto"
            >
              {t('landing.hero.subtitle')}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/register">
                <Button size="lg" className="min-w-[200px] animate-cta-glow">
                  {t('landing.hero.startFreeTrial')}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button variant="secondary" size="lg" className="min-w-[200px]" onClick={() => setShowDemo(true)}>
                <Play className="w-5 h-5 mr-2" />
                {t('landing.hero.watchDemo')}
              </Button>
            </motion.div>

            {/* Trust Badge */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-6 text-sm text-text-tertiary"
            >
              {t('landing.hero.trustBadge')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border-subtle bg-bg-secondary/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-accent-primary mb-1">
                  {stat.value}
                </div>
                <div className="text-text-secondary text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-bg-secondary border border-border-subtle hover:border-accent-primary/50 transition-colors group relative"
              >
                {'badge' in feature && feature.badge && (
                  <span className="absolute top-4 right-4 px-2 py-1 text-xs font-medium bg-accent-primary/20 text-accent-primary rounded-full flex items-center gap-1">
                    <UserCheck className="w-3 h-3" />
                    {feature.badge}
                  </span>
                )}
                <div className="w-14 h-14 rounded-xl bg-accent-primary/10 flex items-center justify-center mb-5 group-hover:bg-accent-primary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-accent-primary" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-3">
                  {feature.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methods Section — 4 trading methodologies */}
      <section id="methods" className="py-20 md:py-32 bg-bg-secondary/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-gold/10 text-accent-gold text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              {t('landing.methods.eyebrow')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
              {t('landing.methods.title')}
            </h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              {t('landing.methods.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {methods.map((method, index) => {
              const { Icon, color } = method;
              return (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className={`relative p-6 md:p-8 rounded-2xl bg-gradient-to-br ${color.bg} border ${color.border} backdrop-blur-sm transition-all hover:shadow-lg group`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-bg-primary/40 flex items-center justify-center ${color.icon}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-semibold text-text-primary mb-1">
                        {method.title}
                      </h3>
                      <p className={`text-sm font-medium ${color.icon}`}>
                        {method.short}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-secondary leading-relaxed mb-5">
                    {method.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {method.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-bg-primary/40 border border-border-subtle text-xs font-medium text-text-secondary"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section — Interactive Journey */}
      <HowItWorksSection steps={steps} t={t} />

      {/* Custom Prompt Section — Pro+ selling point */}
      <section id="prompt-custom" className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-primary/10 text-accent-primary text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                {t('landing.promptCustom.eyebrow')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                {t('landing.promptCustom.title')}
              </h2>
              <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                {t('landing.promptCustom.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {promptFeatures.map((feature, index) => {
                const { Icon } = feature;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="p-6 rounded-2xl bg-bg-secondary border border-border-subtle hover:border-accent-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-accent-primary" />
                    </div>
                    <h3 className="font-semibold text-text-primary mb-2">{feature.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-accent-gold/10 to-accent-primary/5 border border-accent-gold/20"
            >
              <p className="text-text-secondary mb-4">
                {t('landing.promptCustom.proHint')}
              </p>
              <Link to="/pricing">
                <Button variant="secondary" size="lg">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('landing.promptCustom.cta')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative max-w-4xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-accent-primary/10 to-accent-secondary/10 border border-accent-primary/20"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 rounded-3xl bg-accent-primary/5 blur-xl" />

            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                {t('landing.cta.title')}
              </h2>
              <p className="text-xl text-text-secondary mb-8 max-w-xl mx-auto">
                {t('landing.cta.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register">
                  <Button size="lg" className="animate-cta-glow">
                    {t('landing.cta.getStartedFree')}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="secondary" size="lg">
                    {t('landing.cta.viewPricing')}
                  </Button>
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-profit" />
                  {t('landing.cta.freeTrial')}
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-profit" />
                  {t('landing.cta.cancelAnytime')}
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-profit" />
                  {t('landing.cta.noCommitment')}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border-subtle">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.svg" alt={BRAND.displayDomain} className="h-8 w-auto" />
                <div className="flex items-baseline">
                  <span className="font-display font-semibold text-xl tracking-tight text-white">{BRAND.name}</span>
                  <span className="text-accent-primary font-display font-semibold text-xl tracking-tight">.ai</span>
                </div>
              </div>
              <p className="text-text-secondary text-sm">
                {t('landing.footer.brandDescription')}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">{t('landing.footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-text-secondary hover:text-text-primary">{t('landing.nav.features')}</a></li>
                <li><a href="#methods" className="text-text-secondary hover:text-text-primary">{t('landing.nav.methods')}</a></li>
                <li><Link to="/pricing" className="text-text-secondary hover:text-text-primary">{t('landing.nav.pricing')}</Link></li>
                <li><a href="#how-it-works" className="text-text-secondary hover:text-text-primary">{t('landing.nav.howItWorks')}</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">{t('landing.footer.support')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/contact" className="text-text-secondary hover:text-text-primary">{t('landing.footer.contactUs')}</Link></li>
                <li><a href={BRAND.statusUrl} target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-text-primary">{t('landing.footer.status')}</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-text-secondary hover:text-text-primary">{t('landing.footer.privacyPolicy')}</Link></li>
                <li><Link to="/terms" className="text-text-secondary hover:text-text-primary">{t('landing.footer.termsOfService')}</Link></li>
                <li><Link to="/risk-disclaimer" className="text-text-secondary hover:text-text-primary">{t('landing.footer.riskDisclaimer')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-border-subtle flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-x-4 gap-y-1 text-text-tertiary text-sm">
              <p>
                &copy; {new Date().getFullYear()} {BRAND.displayDomain}. {t('landing.footer.allRightsReserved')}
              </p>
              <a
                href="https://www.tradingview.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-text-primary"
              >
                Charts by TradingView
              </a>
            </div>
            <LanguageToggle direction="up" />
          </div>

          {/* Disclaimer */}
          <p className="mt-8 text-xs text-text-tertiary text-center max-w-3xl mx-auto">
            <strong>{t('landing.footer.disclaimerTitle')}</strong> {t('landing.footer.disclaimerText')}
          </p>
        </div>
      </footer>

      {/* Demo Video Modal */}
      <AnimatePresence>
        {showDemo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setShowDemo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <DemoVideo />
              <button
                onClick={() => setShowDemo(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/80 transition-colors z-10"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================
   Demo Video Player (Bunny.net with signed token)
   ============================================================ */

const BUNNY_LIBRARY_ID = import.meta.env.VITE_BUNNY_LIBRARY_ID ?? '';
const BUNNY_VIDEO_ID = import.meta.env.VITE_BUNNY_VIDEO_ID ?? '';
const BUNNY_TOKEN_KEY = import.meta.env.VITE_BUNNY_TOKEN_KEY ?? '';

const BUNNY_CONFIGURED = !!(BUNNY_LIBRARY_ID && BUNNY_VIDEO_ID && BUNNY_TOKEN_KEY);

function DemoVideo() {
  const [embedUrl, setEmbedUrl] = useState('');

  useEffect(() => {
    if (!BUNNY_CONFIGURED) return;
    async function generateSignedUrl() {
      const expires = Math.floor(Date.now() / 1000) + 7200; // 2 hours
      const data = new TextEncoder().encode(`${BUNNY_TOKEN_KEY}${BUNNY_VIDEO_ID}${expires}`);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const token = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
      setEmbedUrl(
        `https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${BUNNY_VIDEO_ID}?token=${token}&expires=${expires}&autoplay=true&preload=true`
      );
    }
    generateSignedUrl();
  }, []);

  if (!BUNNY_CONFIGURED) return null;

  if (!embedUrl) {
    return <div className="w-full h-full bg-bg-tertiary flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <iframe
      src={embedUrl}
      className="w-full h-full"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
}

/* ============================================================
   How It Works — Interactive Animated Journey
   ============================================================ */

const stepIcons = [UserPlus, LineChart, Zap, TrendingUp];

const stepColors = [
  { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
  { bg: 'from-violet-500/20 to-violet-600/10', border: 'border-violet-500', text: 'text-violet-400', glow: 'shadow-violet-500/20' },
  { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
  { bg: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-500', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
];

interface StepData {
  number: string;
  title: string;
  description: string;
}

function HowItWorksSection({ steps, t }: { steps: StepData[]; t: (key: string) => string }) {
  const [activeStep, setActiveStep] = useState(0);

  const goNext = () => setActiveStep((i) => Math.min(steps.length - 1, i + 1));
  const goPrev = () => setActiveStep((i) => Math.max(0, i - 1));

  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-bg-secondary/50 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            {t('landing.howItWorks.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Progress Bar */}
          <div className="relative mb-12">
            <div className="hidden md:block absolute top-6 left-0 right-0 h-0.5 bg-border" />
            <div
              className="hidden md:block absolute top-6 left-0 h-0.5 bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-700 ease-out"
              style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
            />

            <div className="flex justify-between">
              {steps.map((step, index) => {
                const isActive = index === activeStep;
                const isPast = index < activeStep;
                const color = stepColors[index];
                const Icon = stepIcons[index];

                return (
                  <motion.button
                    key={step.number}
                    onClick={() => setActiveStep(index)}
                    className="relative flex flex-col items-center gap-3 z-10 group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Circle */}
                    <motion.div
                      className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive
                          ? `bg-gradient-to-br ${color.bg} ${color.border} border-2 shadow-lg ${color.glow}`
                          : isPast
                          ? 'bg-accent-primary/20 border-2 border-accent-primary'
                          : 'bg-bg-tertiary border-2 border-border group-hover:border-text-tertiary'
                      }`}
                      animate={isActive ? { boxShadow: ['0 0 0 0 rgba(99,102,241,0)', '0 0 0 12px rgba(99,102,241,0.1)', '0 0 0 0 rgba(99,102,241,0)'] } : {}}
                      transition={isActive ? { duration: 2, repeat: Infinity } : {}}
                    >
                      {isPast ? (
                        <Check className="w-5 h-5 text-accent-primary" />
                      ) : (
                        <Icon className={`w-5 h-5 transition-colors ${isActive ? color.text : 'text-text-tertiary group-hover:text-text-secondary'}`} />
                      )}
                    </motion.div>

                    {/* Step label (mobile: hidden, desktop: shown) */}
                    <span className={`hidden md:block text-xs font-medium transition-colors ${
                      isActive ? 'text-text-primary' : 'text-text-tertiary'
                    }`}>
                      {step.number}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Active Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="relative touch-pan-y"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.x < -50) goNext();
                else if (info.offset.x > 50) goPrev();
              }}
            >
              <div className={`relative rounded-2xl p-8 md:p-12 border bg-gradient-to-br ${stepColors[activeStep].bg} ${stepColors[activeStep].border} border-opacity-20`}>
                {/* Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${stepColors[activeStep].bg} blur-xl opacity-30 pointer-events-none`} />

                <div className="relative flex flex-col md:flex-row items-center gap-8">
                  {/* Icon */}
                  <motion.div
                    className={`flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br ${stepColors[activeStep].bg} flex items-center justify-center`}
                    initial={{ rotate: -10, scale: 0.8 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  >
                    {(() => { const Icon = stepIcons[activeStep]; return <Icon className={`w-10 h-10 ${stepColors[activeStep].text}`} />; })()}
                  </motion.div>

                  {/* Text */}
                  <div className="text-center md:text-left flex-1">
                    <motion.div
                      className={`inline-block text-xs font-bold uppercase tracking-wider ${stepColors[activeStep].text} mb-2`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      {t('landing.howItWorks.stepLabel')} {steps[activeStep].number}
                    </motion.div>
                    <motion.h3
                      className="text-2xl md:text-3xl font-bold text-text-primary mb-3"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      {steps[activeStep].title}
                    </motion.h3>
                    <motion.p
                      className="text-lg text-text-secondary max-w-lg"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      {steps[activeStep].description}
                    </motion.p>
                  </div>

                  {/* Step counter */}
                  <div className="flex-shrink-0 hidden md:block">
                    <span className={`text-7xl font-black ${stepColors[activeStep].text} opacity-20`}>
                      {steps[activeStep].number}
                    </span>
                  </div>
                </div>

              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation dots — outside AnimatePresence so clicks always work */}
          <div className="flex justify-center gap-2 mt-8">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === activeStep ? 'w-8 bg-gradient-to-r from-accent-primary to-accent-secondary' : 'w-2 bg-border hover:bg-text-tertiary'
                }`}
              />
            ))}
          </div>

          {/* Prev / Next buttons — outside AnimatePresence */}
          <div className="flex justify-between mt-4 max-w-5xl mx-auto">
            <button
              onClick={goPrev}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeStep === 0 ? 'opacity-0 pointer-events-none' : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
              }`}
            >
              ← {t('landing.howItWorks.prev')}
            </button>
            <button
              onClick={goNext}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeStep === steps.length - 1 ? 'opacity-0 pointer-events-none' : 'text-accent-primary hover:bg-accent-primary/10'
              }`}
            >
              {t('landing.howItWorks.next')} →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
